import { Component, ElementRef, EventEmitter, Input, OnInit, Output, SimpleChanges, TemplateRef, ViewChild } from '@angular/core';
import { RegionService } from 'shared-lib';
import { ToastrService } from 'ngx-toastr';
import { getInitRegion, Region } from 'shared-lib';
import { ThemeService } from 'ng2-charts';
import { UtilitySharedService } from '../../../shared/_services/utility-shared.service';

@Component({
  selector: 'app-crud-region',
  templateUrl: './crud-region.component.html',
  styleUrl: './crud-region.component.scss',
  standalone: false
})
export class CrudRegionComponent implements OnInit {
  @Input() downloadClicked: boolean = false;
  @Input() reloadTableInput: boolean = false;

  regions: Region[] = [];
  count: number = 0;
  currentPage: number = 1;
  pageSize: number = 10;
  selectedRegion: Region = getInitRegion();
  utilityId: number;
  searchValue: string = "";
  isEditMode: boolean = false;

  @ViewChild('filterTemplate', { static: true })
  filterTemplate!: TemplateRef<any>;

  @Output() filterTemplateReady = new EventEmitter<TemplateRef<any>>();

  @Input() set refreshTrigger(_: number) {
    this.filterTemplateReady.emit(this.filterTemplate);
  }

  @ViewChild('closeEditModal') closeEditModal: ElementRef;
  
  constructor(
    private regionService: RegionService,
    private toastr: ToastrService,
    private utilitySharedService: UtilitySharedService
  ) {
    this.utilityId = Number(this.utilitySharedService.getUtilityId());
  }

  ngOnInit(): void {
    this.filterTemplateReady.emit(this.filterTemplate);
    this.reloadTable(1);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['reloadTableInput']) {
      setTimeout(() => {
        this.reloadTable(1);
      }, 1000);
    }
  }

  resetFields() {
    this.selectedRegion = getInitRegion();
  }

  openAddModal() {
    this.isEditMode = false;
    this.resetFields();
  }

  openEditModal() {
    this.isEditMode = true;
    // Ne treba fillInputFields() jer se direktno koristi selectedRegion
  }

  private getAllRegions(dataIn: any) {
    this.regionService.getAll(dataIn).subscribe({
      next: (response) => {
        const responseData = response.body;
        if (responseData?.status === 200 || responseData?.status === 'OK') {
          this.regions = responseData.data.data;
          this.count = responseData.data.count;
        } else {
          this.toastr.error(responseData?.message);
        }
      },
      error: (_) => {
        this.toastr.error('Error occurred');
      },
    });
  }

  onSearch() {
    this.currentPage = 1;
    let dataIn = {
      pageInfo: {
        page: this.currentPage,
        pageSize: this.pageSize,
      },
      filterParams: {
        searchValue: this.searchValue
      },
      utilityId: this.utilityId
    };
    this.getAllRegions(dataIn);
  }

  onSearchChange() {
    if (this.searchValue === '' || this.searchValue.trim() === '') {
      this.onSearch();
    }
  }

  reloadTable(page: number | null = null) {
    if (page) this.currentPage = page;
    let dataIn = {
      pageInfo: {
        page: this.currentPage,
        pageSize: this.pageSize,
      },
      filterParams: {
        searchValue: this.searchValue
      },
      utilityId: this.utilityId
    };
    this.getAllRegions(dataIn);
  }

  onPageChange(newPage: number) {
    this.currentPage = newPage;
    this.reloadTable(newPage);
  }

  save() {
    // Validacija direktno preko selectedRegion objekta
    if (!this.selectedRegion.name || this.selectedRegion.name.trim() === '') {
      this.toastr.warning("Name can't be empty!");
      return;
    }
    if (!this.selectedRegion.code || this.selectedRegion.code.trim() === '') {
      this.toastr.warning("Code can't be empty!");
      return;
    }
    if (!this.selectedRegion.number || this.selectedRegion.number.trim() === '') {
      this.toastr.warning("Number can't be empty!");
      return;
    }
    const isNumberValid = /^7\d{3}$/.test(this.selectedRegion.number);
    if (!isNumberValid) {
      this.toastr.warning("Number must be 4 digits and to start with 7!");
      return;
    }
    
    // Validacija koordinata
    if ((this.selectedRegion.latitude === null || this.selectedRegion.latitude === undefined) && 
        (this.selectedRegion.longitude !== null && this.selectedRegion.longitude !== undefined)) {
      this.toastr.warning("You must enter latitude if longitude is provided.");
      return;
    }
    if ((this.selectedRegion.longitude === null || this.selectedRegion.longitude === undefined) && 
        (this.selectedRegion.latitude !== null && this.selectedRegion.latitude !== undefined)) {
      this.toastr.warning("You must enter longitude if latitude is provided.");
      return;
    }

    if (this.selectedRegion.latitude !== null && this.selectedRegion.latitude !== undefined) {
      const latitudeValue = Number(this.selectedRegion.latitude);
      if (isNaN(latitudeValue) || latitudeValue < -90 || latitudeValue > 90) {
        this.toastr.warning("Latitude must be a valid number between -90 and 90!");
        return;
      }
    }

    if (this.selectedRegion.longitude !== null && this.selectedRegion.longitude !== undefined) {
      const longitudeValue = Number(this.selectedRegion.longitude);
      if (isNaN(longitudeValue) || longitudeValue < -180 || longitudeValue > 180) {
        this.toastr.warning("Longitude must be a valid number between -180 and 180!");
        return;
      }
    }

    // Postavi utilityId i id
    this.selectedRegion.utilityId = this.utilityId;
    this.selectedRegion.id = this.isEditMode ? this.selectedRegion.id : 0;

    this.regionService.save(this.selectedRegion).subscribe({
      next: (response) => {
        if (response.body.status === 200 || response.body.status === 'OK') {
          this.toastr.success(this.isEditMode ? "Region successfully updated." : "Region successfully added.");
          this.closeEditModal?.nativeElement?.click();
          setTimeout(() => {
            this.reloadTable(1);
          }, 500);
        } else {
          this.toastr.error(response.body.message || 'Failed to save region');
        }
      },
      error: (_) => {
        this.toastr.error('Error occurred while saving region');
      },
    });
  }

  selectRegion(region: Region) {
    // Kreiranje kopije objekta da se izbegnu neželjene promene originalnog objekta u nizu
    this.selectedRegion = { ...region };
    this.openEditModal();
  }

  onDeleteRegion(region: Region) {
    this.selectedRegion = region;
    this.isEditMode = false;
  }

  deleteRegion() {
    this.regionService.delete(this.selectedRegion.id).subscribe({
      next: (response) => {
        const responseData = response.body;
        if (responseData?.status === 200 || responseData?.status === 'OK') {
          this.toastr.success(responseData?.message);
          this.reloadTable(1);
        } else {
          this.toastr.error(responseData?.message);
        }
      },
      error: (_) => {
        this.toastr.error('Error occurred');
      },
    });
  }
}